import { useCallback, useState, type FormEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getSelfServiceListBearersQueryKey,
  useSelfServiceRenameTravellerBearer,
} from '@/api/trafikportalen';
import { getApiErrorMessage } from '@/helpers/apiError';
import { formatCardNumber } from '@/helpers/formatting';
import { toast } from '@/hooks/useToast';
import { IconCheck } from '@/icons';
import Button from '../core/Button';
import Input from '../core/Input';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';

interface EditTravelCardDialogProps {
  travellerId: string;
  bearerId: string;
  serial?: string;
  currentName?: string;
  onOpenChange: (open: boolean) => void;
}

export const EditTravelCardDialog = ({
  travellerId,
  bearerId,
  serial,
  currentName,
  onOpenChange,
}: EditTravelCardDialogProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const renameBearer = useSelfServiceRenameTravellerBearer();
  const [name, setName] = useState(currentName ?? '');
  const nameId = `card-name-${bearerId}`;
  const trimmedName = name.trim();

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!trimmedName) {
        return;
      }

      if (trimmedName === (currentName ?? '').trim()) {
        onOpenChange(false);
        return;
      }

      try {
        await renameBearer.mutateAsync({
          travellerID: travellerId,
          mtbBearerID: bearerId,
          data: { name: trimmedName },
        });

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Kortet uppdaterat',
            }),
          },
          { autoClose: 3000 },
        );

        await queryClient.invalidateQueries({
          queryKey: getSelfServiceListBearersQueryKey(),
        });

        onOpenChange(false);
      } catch (error) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: getApiErrorMessage(
              error,
              formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            ),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      bearerId,
      currentName,
      formatMessage,
      onOpenChange,
      queryClient,
      renameBearer,
      travellerId,
      trimmedName,
    ],
  );

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <FormSection editMode={true}>
        {serial ? (
          <FormElement
            label={<FormattedMessage defaultMessage="Kortnummer" />}
            input={
              <span className="font-mono text-sm">
                {formatCardNumber(serial)}
              </span>
            }
          />
        ) : null}
        <FormElement
          id={nameId}
          label={<FormattedMessage defaultMessage="Namn på kort" />}
          input={
            <Input
              id={nameId}
              type="text"
              value={name}
              maxLength={100}
              required
              onChange={(event) => setName(event.target.value)}
            />
          }
        />
      </FormSection>
      <div className="mt-4 flex gap-2">
        <Button
          variant="ghost"
          className="ml-auto"
          type="button"
          onClick={() => onOpenChange(false)}
        >
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button
          type="submit"
          variant="filled"
          startIcon={<IconCheck />}
          disabled={!trimmedName}
          loading={renameBearer.isPending}
        >
          <FormattedMessage defaultMessage="Uppdatera" />
        </Button>
      </div>
    </form>
  );
};
